import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Wine, Loader2, MessageSquare, Users, Minus, Plus, Search } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;
const inputCls = "w-full bg-black/50 border border-white/20 px-4 py-3 text-sm text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors";

const Qty = ({ id, value, set, max, disabled }) => (
  <div className="flex items-center gap-2">
    <button onClick={() => set(Math.max(0, value - 1))} disabled={disabled || !value} data-testid={`qty-minus-${id}`}
      className="w-7 h-7 border border-white/20 text-[#A19D94] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-30 flex items-center justify-center">
      <Minus className="w-3 h-3" />
    </button>
    <span className="w-4 text-center text-sm text-[#FDFBF7] tabular-nums" data-testid={`qty-value-${id}`}>{value}</span>
    <button onClick={() => set(Math.min(max, value + 1))} disabled={disabled || value >= max} data-testid={`qty-plus-${id}`}
      className="w-7 h-7 border border-white/20 text-[#A19D94] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-30 flex items-center justify-center">
      <Plus className="w-3 h-3" />
    </button>
  </div>
);

const ReservationCard = ({ res, menu, email, onUpdated }) => {
  const locked = res.balance_paid || res.cancelled;
  const initQty = {};
  (res.bottles || []).forEach((b) => { initQty[b.id] = (initQty[b.id] || 0) + b.qty; });
  const [bottleQty, setBottleQty] = useState(initQty);
  const [busy, setBusy] = useState("");

  const bottleTotal = Object.entries(bottleQty).reduce((sum, [id, q]) => {
    const item = menu.flatMap((c) => c.items).find((i) => i.id === id);
    return sum + (item ? item.price * q : 0);
  }, 0);
  const bottleCount = Object.values(bottleQty).reduce((a, b) => a + b, 0);

  const saveBottles = async () => {
    setBusy("bottles");
    try {
      const { data } = await api.post(`/sections/${res.session_id}/bottles`, {
        email,
        bottles: Object.entries(bottleQty).filter(([, q]) => q > 0).map(([id, qty]) => ({ id, qty })),
      });
      onUpdated(data);
      toast.success(`Bottles updated — your balance is now ${fmt(data.balance_due_cents)}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update bottles.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="border border-white/10 bg-black/40 p-6 md:p-8 text-left" data-testid={`reservation-card-${res.session_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-white/10">
        <div>
          <h2 className="font-serif-display text-2xl text-[#FDFBF7]" data-testid="reservation-section-name">{res.section_name}</h2>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#A19D94] mt-1">
            <Users className="w-3.5 h-3.5 text-[#D4AF37]" /> Party of {res.party_size} · Order <span className="text-[#F4D77E]" data-testid="reservation-order-no">{res.reservation_no}</span>
          </div>
        </div>
        {res.balance_paid ? (
          <span className="text-[10px] uppercase tracking-[0.2em] border border-[#D4AF37] bg-[#D4AF37]/15 text-[#F4D77E] px-3 py-1.5" data-testid="reservation-settled">Settled</span>
        ) : res.cancelled ? (
          <span className="text-[10px] uppercase tracking-[0.2em] border border-red-400/40 text-red-300 px-3 py-1.5" data-testid="reservation-cancelled">Cancelled</span>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.2em] border border-white/15 text-[#A19D94] px-3 py-1.5" data-testid="reservation-open">Balance open</span>
        )}
      </div>

      {locked && (
        <p className="text-sm text-[#A19D94] mt-5" data-testid="reservation-locked-note">
          {res.balance_paid
            ? "Your balance is paid — bottles are locked in. Need a change? Reach out to Jay's team."
            : "This reservation was cancelled. Reach out to Jay's team if you think this is a mistake."}
        </p>
      )}

      {/* Bottles */}
      <div className="mt-6" data-testid="bottle-editor">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">
          Your bottles · minimum {res.min_bottles} (mixers included)
        </div>
        {menu.map((cat) => (
          <div key={cat.category} className="mb-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8C7324] mb-2">{cat.category}</div>
            <div className="space-y-1.5">
              {cat.items.map((item) => (
                <div key={item.id} className="flex items-center justify-between border border-white/5 bg-black/30 px-4 py-2.5" data-testid={`bottle-row-${item.id}`}>
                  <span className="text-sm text-[#FDFBF7]">{item.name} <span className="text-[#A19D94]">· {fmt(item.price)}</span></span>
                  <Qty id={`bottle-${item.id}`} value={bottleQty[item.id] || 0}
                    set={(v) => setBottleQty((q) => ({ ...q, [item.id]: v }))} max={10} disabled={locked} />
                </div>
              ))}
            </div>
          </div>
        ))}
        {!locked && (
          <button onClick={saveBottles} disabled={busy === "bottles" || bottleCount < res.min_bottles} data-testid="bottle-save-button"
            className="mt-2 w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] border border-[#D4AF37]/60 text-[#D4AF37] px-6 py-3 hover:bg-[#D4AF37] hover:text-black transition-colors duration-300 disabled:opacity-40">
            {busy === "bottles" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wine className="w-4 h-4" />}
            {busy === "bottles" ? "Saving…" : bottleCount < res.min_bottles ? `Pick at least ${res.min_bottles} bottle${res.min_bottles > 1 ? "s" : ""}` : `Save Bottles — ${fmt(bottleTotal)}`}
          </button>
        )}
      </div>

      {/* Balance */}
      <div className="mt-8 pt-6 border-t border-white/10" data-testid="balance-summary">
        <div className="flex justify-between text-sm py-1.5">
          <span className="text-[#A19D94]">Deposit already paid</span>
          <span className="text-green-300">{fmt(res.deposit_cents)}</span>
        </div>
        <div className="flex justify-between text-sm py-1.5">
          <span className="text-[#A19D94]">Bottles</span>
          <span className="text-[#FDFBF7]" data-testid="summary-bottles">{fmt(res.bottles_cents ?? (res.balance_due_cents - (res.hookah_cents || 0)))}</span>
        </div>
        {(res.hookahs || 0) > 0 && (
          <div className="flex justify-between text-sm py-1.5">
            <span className="text-[#A19D94]">Hookah × {res.hookahs}</span>
            <span className="text-[#FDFBF7]" data-testid="summary-hookah">{fmt(res.hookah_cents)}</span>
          </div>
        )}
        <div className="flex justify-between items-center py-3 mt-2 border-t border-white/10">
          <span className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Balance due {res.balance_due_date}</span>
          <span className="font-serif-display text-2xl text-[#F4D77E]" data-testid="balance-due-amount">{fmt(res.balance_due_cents)}</span>
        </div>
        {!res.balance_paid && !res.cancelled && (
          <>
            <a href={`sms:+13239261750?&body=${encodeURIComponent(`Hi! I'd like to Apple Pay my section balance for ${res.section_name} — order ${res.reservation_no || ""}`)}`}
              data-testid="manage-pay-button"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-4 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300">
              <MessageSquare className="w-4 h-4" /> Text To Apple Pay {fmt(res.balance_due_cents)}
            </a>
            <p className="text-xs text-[#A19D94] mt-3 text-center" data-testid="manage-pay-note">
              Text <span className="text-[#F4D77E]">{res.balance_phone || "(323) 926-1750"}</span> to Apple Pay your balance — mention order <span className="text-[#F4D77E]">{res.reservation_no}</span>.
            </p>
            <p className="text-[10px] text-[#5c5850] mt-3 text-center">Bottle edits lock once your balance is paid. Admission tickets sold separately.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default function ReservationManage() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("e") || "");
  const [data, setData] = useState(null);
  const [state, setState] = useState("form"); // form | loading | ready

  const lookup = async (em) => {
    const target = (em ?? email).trim();
    if (!target.includes("@")) {
      toast.error("Enter the email you booked with.");
      return;
    }
    setState("loading");
    try {
      const { data } = await api.post("/sections/lookup", { email: target });
      setData(data);
      setState("ready");
    } catch (err) {
      setData(null);
      setState("form");
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No reservation found for that email.");
    }
  };

  useEffect(() => {
    const e = params.get("e");
    if (e && e.includes("@")) lookup(e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRes = (updated) => {
    setData((d) => ({ ...d, reservations: d.reservations.map((r) => r.session_id === updated.session_id ? updated : r) }));
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-start justify-center px-6 py-16" data-testid="reservation-page">
      <div className="grain-overlay" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-2xl">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-3">
            <img src="/logo-jayatk.png" alt="Jay Atk" className="h-9 w-auto" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8C7324]">Manage Your Section</div>
          <h1 className="font-serif-display text-4xl text-[#FDFBF7] mt-3">Your reservation, <span className="italic text-gradient-gold">your way.</span></h1>
        </div>

        {state !== "ready" && (
          <div className="border border-white/10 bg-black/40 backdrop-blur-xl p-8 gold-glow" data-testid="reservation-lookup-form">
            <p className="text-sm text-[#A19D94] mb-5 text-center">Enter the email you booked with to see your section, switch bottles, or pay your balance.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email you booked with"
                className={inputCls} data-testid="reservation-email-input"
                onKeyDown={(e) => e.key === "Enter" && lookup()} />
              <button onClick={() => lookup()} disabled={state === "loading"} data-testid="reservation-lookup-button"
                className="shrink-0 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-3 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                {state === "loading" ? "Finding…" : "Find My Reservation"}
              </button>
            </div>
          </div>
        )}

        {state === "ready" && data && (
          <div className="space-y-6" data-testid="reservation-results">
            {data.reservations.map((r) => (
              <ReservationCard key={r.session_id} res={r} menu={data.bottle_menu} email={email} onUpdated={updateRes} />
            ))}
            <button onClick={() => { setData(null); setState("form"); }} data-testid="reservation-start-over"
              className="w-full text-[11px] uppercase tracking-[0.2em] text-[#5c5850] hover:text-[#D4AF37] transition-colors py-2">
              Look up a different email
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}
